import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { keyframes } from '@emotion/react';

import { containerStyles } from '../../styles/layout';
import { headers } from '../../styles/type';
import { dotPatternStyles } from '../../styles/backgrounds';
import { rem } from '../../utilities/style';
import { fontWeights } from '../../theme/type';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';

import Image from '../Image';
import ButtonGroup from '../ButtonGroup';
import Button from '../buttons/Button';
import ButtonText from '../buttons/ButtonText';
import TextRenderer from '../TextRenderer';

const Container = styled.div``;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Content = styled.div`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column-reverse;
  padding-top: ${rem(24)};
  padding-bottom: ${rem(48)};

  ${media('>small')} {
    padding-bottom: ${rem(80)};
    flex-direction: row;
    align-items: flex-start;
  }

  &::before {
    ${dotPatternStyles}
    content: '';
    position: absolute;
    z-index: -1;
    display: block;
    top: 0;
    left: ${rem(-20)};
    width: ${rem(180)};
    height: ${rem(56)};

    ${media('>small')} {
      left: ${rem(-60)};
    }
  }
`;

const TextContent = styled.div`
  ${media('>small')} {
    flex: 0 1 ${rem(407)};
    margin-right: ${rem(5)};
  }
`;

const Headline = styled.h1`
  ${headers.h3}
  margin-bottom: ${rem(32)};
  color: ${colors.blue['800']};

  ${media('>small')} {
    margin-top: ${rem(35)};
  }
`;

const ImageContent = styled.div`
  position: relative;
  aspect-ratio: 480 / 512;
  max-width: ${rem(480)};
  margin-bottom: ${rem(16)};

  ${media('>small')} {
    flex: 0 1 ${rem(480)};
    margin-left: auto;
    margin-bottom: 0;
  }

  ${media('>smedium')} {
    margin-right: ${rem(40)};
    margin-left: 0;
  }
`;

const StyledImage = styled(Image)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  height: 100%;
  width: 100%;
  object-fit: contain;
  opacity: 1;
  transition: opacity 200ms ease;

  ${({ currentImage }) => !currentImage && `
    opacity: 0;
  `}
`;

const Stats = styled.div`
  flex: 0 1 ${rem(292)};
  display: flex;
  flex-direction: column;
  gap: ${rem(56)};
  margin-left: auto;

  ${media('<=smedium')} {
    display: none;
  }
`;

const Stat = styled.div``;

const StatMetric = styled.div`
  font-size: ${rem(48)};
  font-weight: ${fontWeights.bold};
  line-height: 1.15;
  color: ${colors.blue['800']};

  ${media('>smedium')} {
    font-size: ${rem(62)};
  }

  ${media('>medium')} {
    font-size: ${rem(72)};
  }
`;

const StatText = styled.div`
  color: ${colors.gray['600']};
`;

const Scroller = styled.div`
  z-index: 0;
  margin-top: ${rem(40)};
  position: relative;
  height: ${rem(45)};
  overflow: hidden;

  ${media('>small')} {
    margin-top: ${rem(120)};
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    z-index: 1;
    top: 0;
    width: ${rem(80)};
    height: 100%;
  }

  &::before {
    left: 0;
    background-image: linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%);
  }

  &::after {
    right: 0;
    background-image: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
  }
`;

const scroll = keyframes`
  to {
    transform: translateX(calc(-50% - (40px / 2)));
  }
`;

const ScrollerTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  display: flex;
  gap: ${rem(40)};
  align-items: center;
  transform: translate(0);
  max-width: none;
  width: max-content;
  animation-name: ${scroll};
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  ${({ animationDur }) => `
    animation-duration: ${animationDur}s;
  `}
`;

const ScrollerImage = styled(Image)`
  display: block;
  height: 100%;
  width: auto;
`;

const Logos = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  padding: ${rem(24)} 0;
  gap: ${rem(40)};

  ${media('<=small')} {
    display: none;
  }

  ${media('>medium')} {
    gap: ${rem(64)};
  }

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 50%;
    transform: translateX(-50%);
    width: 100vw;
    height: 1px;
    background-color: ${colors.gray['100']};
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Logo = styled(Image)`
  width: auto;
  max-height: ${rem(45)};
`;

const SocialProof = ({
  headline,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  heroImages,
  scrollImages,
  stat1Metric,
  stat1Text,
  stat2Metric,
  stat2Text,
  stat3Metric,
  stat3Text,
  logos
}) => {
  const [imageIndex, setImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndex((prevIndex) => {
        const newIndex = prevIndex + 1;
        return newIndex >= heroImages?.length ? 0 : newIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [heroImages?.length]);

  return (
    <Container>
      <ContentWrapper>
        <Content>
          <TextContent>
            {headline && <Headline><TextRenderer text={headline} /></Headline>}
            <ButtonGroup>
              {primaryCtaText && primaryCtaLink && (
                <Button to={primaryCtaLink}>
                  <TextRenderer text={primaryCtaText} />
                </Button>
              )}
              {secondaryCtaText && secondaryCtaLink && (
                <ButtonText color="dark" to={secondaryCtaLink}>
                  <TextRenderer text={secondaryCtaText} />
                </ButtonText>
              )}
            </ButtonGroup>
            {scrollImages && scrollImages.length > 0 && (
              <Scroller>
                <ScrollerTrack animationDur={scrollImages.length * 3}>
                  {[...scrollImages, ...scrollImages].map((scrollImage, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ScrollerImage key={index} image={scrollImage} alt={scrollImage?.description} />
                  ))}
                </ScrollerTrack>
              </Scroller>
            )}
          </TextContent>
          {heroImages && heroImages.length > 0 && (
            <ImageContent>
              {heroImages.map((heroImage, index) => (
                <StyledImage
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  image={heroImage}
                  alt={heroImage?.description}
                  currentImage={index === imageIndex}
                  objectFit="contain"
                />
              ))}
            </ImageContent>
          )}
          {(stat1Metric || stat2Metric || stat3Metric || stat1Text || stat2Text || stat3Text) && (
            <Stats>
              <Stat>
                {stat1Metric && (
                  <StatMetric>
                    <TextRenderer text={stat1Metric} />
                  </StatMetric>
                )}
                {stat1Text && (
                  <StatText>
                    <TextRenderer text={stat1Text} />
                  </StatText>
                )}
              </Stat>
              <Stat>
                {stat2Metric && (
                  <StatMetric>
                    <TextRenderer text={stat2Metric} />
                  </StatMetric>
                )}
                {stat2Text && (
                  <StatText>
                    <TextRenderer text={stat2Text} />
                  </StatText>
                )}
              </Stat>
              <Stat>
                {stat3Metric && (
                  <StatMetric>
                    <TextRenderer text={stat3Metric} />
                  </StatMetric>
                )}
                {stat3Text && (
                  <StatText>
                    <TextRenderer text={stat3Text} />
                  </StatText>
                )}
              </Stat>
            </Stats>
          )}
        </Content>
        {logos && logos.length > 0 && (
          <Logos>
            {logos.map((logo, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <LogoWrapper key={index}>
                <Logo image={logo} alt={logo?.description} />
              </LogoWrapper>
            ))}
          </Logos>
        )}
      </ContentWrapper>
    </Container>
  );
};

SocialProof.propTypes = {
  headline: PropTypes.string,
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  heroImages: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object,
      description: PropTypes.string
    })
  ),
  scrollImages: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object,
      description: PropTypes.string
    })
  ),
  stat1Metric: PropTypes.string,
  stat1Text: PropTypes.string,
  stat2Metric: PropTypes.string,
  stat2Text: PropTypes.string,
  stat3Metric: PropTypes.string,
  stat3Text: PropTypes.string,
  logos: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object,
      description: PropTypes.string
    })
  )
};

export const query = graphql`
  fragment SocialProofFragment on ContentfulSocialProof {
    id
    __typename
    headline
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
    heroImages {
      gatsbyImageData(width: 480, quality: 75)
      ...ImageAssetFragment
    }
    scrollImages {
      gatsbyImageData(height: 45, quality: 75)
      ...ImageAssetFragment
    }
    logos {
      gatsbyImageData(height: 45, quality: 75)
      ...ImageAssetFragment
    }
    stat1Metric
    stat1Text
    stat2Metric
    stat2Text
    stat3Metric
    stat3Text
  }
`;

export default SocialProof;
